import apiClient from "../../client/apiClient"
import { appendQueryParams } from "../../utils"

type Section = {
  name: string
  _id: string
}

export interface Room {
  _id: string;
  name: string;
}
export type Sections = Section[]

export type Class = {
  _id: string
  academicYear: string
  branch: string
  branchId: string
  capacity: number
  classIdentifier?: string
  createdAt: string
  createdBy?: string
  name: string
  sections: Sections
  status: "active" | "inactive"
  subjects: string[]
  students?: string[]
  registrationType: "interview+test" | "interview"
  stage?: string
  // Academic class group (Group model), resolved server-side via reverse
  // lookup on Group.classes[] — used to bucket classes where `stage` is unset.
  classGroup?: { _id: string; name: string } | null
  updatedAt: string
  sectionDetails:string
  system: string
   room?: Room | null;
  session: {
    _id: string
  }
  order: number
  course:string[]
}

export type PaginationData = {
  currentPage: number
  hasNextPage: boolean
  hasPrevPage: boolean
  limit: number
  total: number
  totalPages: number
}

export type ClassUpdateData = {
  name: string
  sections: Sections
  capacity: number
  registrationType: "interview+test" | "interview"
  stage?: string
  branchId: string
  status: "active" | "inactive"
  system: string
  // Which session this save's sections belong to — lets the backend replace
  // only this campus+session's slice of the shared sections array instead of
  // the whole thing.
  session?: string
}

export type ApiResponse = {
  success: boolean;
  classes: Class[]
  pagination: PaginationData
}

const CLASS_ENDPOINT = {
  CLASSES: "/classes",
  SECTIONS: "/sections",
}
const getAllClasses = async ({
  page = 1,
  limit = 20,
  sortBy,
  sortOrder,
  searchText: search,
  class: appliedClass,
  status,
  sections,
  startDate,
  endDate,
  system,
  registrationType,
  branch,
  all,
  session,
}: {
  page?: number
  limit?: number
  sortBy?: string
  sortOrder?: string
  searchText?: string
  class?: string
  status?: string
  sections?: string
  startDate?: string
  endDate?: string
  system?: string
  registrationType?: string
  branch?: string
  // Bypass the campus's Branch.systems scoping — for cross-campus admin
  // screens (e.g. course-structure builder) that pick a campus explicitly
  // rather than relying on the globally-selected one.
  all?: string
  // Which sections show up per class varies by session (a class can run
  // different sections year to year) — pass the current session so the
  // returned class.sections is scoped to it, not every session at once.
  session?: string
} = {}): Promise<ApiResponse> => {
  const response = await apiClient({
    url: appendQueryParams(CLASS_ENDPOINT.CLASSES, {
      page,
      limit,
      sortBy,
      sortOrder,
      searchText: search,
      class: appliedClass,
      status,
      sectionId: sections,
      startDate,
      endDate,
      system,
      registrationType,
      branch,
      all,
      session,
    }),
    method: "GET",
  })
  return response.data
}
const createClass = async (data: any): Promise<Class> => {
  const response = await apiClient({
    url: CLASS_ENDPOINT.CLASSES,
    method: "POST",
    data,
  })
  return response.data
}


const updateClass = async (
  _id: string,
  data: Omit<ClassUpdateData, "sections"> & { sections: string[] }
) => {
  const response = await apiClient({
    url: `${CLASS_ENDPOINT.CLASSES}/${_id}`,
    method: "PUT",
    data,
  })

  return response.data
}

const deleteClasses = async (_id: string) => {
  const response = await apiClient({
    url: `${CLASS_ENDPOINT.CLASSES}/${_id}`,
    method: "DELETE",
  })

  return response.data
}

const getAllSections = async (params?: {
  session?: string
  all?: string
  branch?: string
  limit?: number
}) => {
  const response = await apiClient({
    url: appendQueryParams(CLASS_ENDPOINT.SECTIONS, params || {}),
    method: "GET",
  })

  return response.data
}

// Create a section. The backend creates it for ALL campuses for the given
// session (idempotent), so it becomes available everywhere.
const createSection = async (data: { name: string; session: string; status?: string }) => {
  const response = await apiClient({
    url: CLASS_ENDPOINT.SECTIONS,
    method: "POST",
    data: { status: "active", ...data },
  })
  return response.data
}

// Delete a section (blocked by the backend if any student is enrolled in it).
const deleteSection = async (id: string) => {
  const response = await apiClient({
    url: `${CLASS_ENDPOINT.SECTIONS}/${id}`,
    method: "DELETE",
  })
  return response.data
}

// Rename / toggle status of one section. Only touches THIS campus's own copy
// (the backend rejects it if the section belongs to a different branch) —
// other campuses' same-named sections are separate documents and unaffected.
const updateSection = async (id: string, data: { name?: string; status?: "active" | "inactive" }) => {
  const response = await apiClient({
    url: `${CLASS_ENDPOINT.SECTIONS}/${id}`,
    method: "PUT",
    data,
  })
  return response.data
}

export {
  getAllClasses,
  createClass,
  updateClass,
  deleteClasses,
  getAllSections,
  createSection,
  updateSection,
  deleteSection,
}
